import React, { useCallback, useState, useRef, useEffect } from "react";
import GridContainer from "common/components/Grid/GridContainer.jsx";
import GridItem from "common/components/Grid/GridItem.jsx";
import Card from "common/components/Card/Card.jsx";
import CardHeader from "common/components/Card/CardHeader.jsx";
import CardIcon from "common/components/Card/CardIcon.jsx";
import CardBody from "common/components/Card/CardBody.jsx";
import LoadingBox from "common/components/LoadingBox";

import PostAddIcon from "@material-ui/icons/PostAdd";

import Button from "common/components/CustomButtons/Button.jsx";

import config from "config.js";
import { Link } from "react-router-dom";
import store from "store";
import dashboardStyle from "assets/jss/material-dashboard-pro-react/views/dashboardStyle";
import withStyles from "@material-ui/core/styles/withStyles";
import { pickNativeIosPhoto } from "../../nativePhotoPicker";
import { isNativeIosPlatform } from "../../deviceInfo";

import loadContent from "core-module/hoc/loadContent";

const importedStyle = dashboardStyle;
const styles = {
  cardCategory: importedStyle.cardCategory,
  cardTitle: { ...importedStyle.cardTitle, margin: "30px", padding: "20px" },
  cardIconTitle: importedStyle.cardIconTitle,
  stats: importedStyle.stats,
  fileInput: {
    display: "none",
  },
  buttonStyle: {
    margin: "30px",
  },
};
const MAX_UPLOAD_SIZE = 10000000;
const JPEG_CONVERSION_QUALITY = 0.85;

function isJpegFile(file) {
  const fileType = (file.type || "").toLowerCase();
  const selectedFileName = (file.name || "").toLowerCase();

  return (
    fileType === "image/jpeg" ||
    fileType === "image/jpg" ||
    selectedFileName.endsWith(".jpg") ||
    selectedFileName.endsWith(".jpeg")
  );
}

function isPngFile(file) {
  const fileType = (file.type || "").toLowerCase();
  const selectedFileName = (file.name || "").toLowerCase();

  return fileType === "image/png" || selectedFileName.endsWith(".png");
}

function convertPngToJpeg(file) {
  return new Promise((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      const context = canvas.getContext("2d");

      if (context == null) {
        URL.revokeObjectURL(imageUrl);
        reject(new Error("Could not prepare image for upload."));
        return;
      }

      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);
      URL.revokeObjectURL(imageUrl);
      canvas.toBlob(
        (jpegBlob) => {
          if (jpegBlob == null) {
            reject(new Error("Could not convert image to JPEG."));
            return;
          }

          const selectedFileName = file.name || "document.png";
          const jpegFileName = selectedFileName.replace(/\.png$/i, ".jpg");
          resolve(new File([jpegBlob], jpegFileName, { type: "image/jpeg" }));
        },
        "image/jpeg",
        JPEG_CONVERSION_QUALITY
      );
    };

    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error("Could not read image for upload."));
    };
    image.src = imageUrl;
  });
}

async function normalizeUploadFile(file) {
  if (isJpegFile(file)) {
    return file;
  }

  if (isPngFile(file)) {
    return convertPngToJpeg(file);
  }

  return null;
}

//import Contrac from "./AutoPaymentSummary.jsx";

const DocumentUpload = (props) => {
  const [buttonDisable, setButtonDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [nativeFile, setNativeFile] = useState(null);
  const [documentStatus, setDocumentStatus] = useState("I");
  const [pendingphotos, setPendingphotos] = useState(0);

  const fileRef = useRef(null);
  const { classes, getList } = props;
  const useNativeIosUpload = isNativeIosPlatform();

  const onInputChange = useCallback(() => {
    if (useNativeIosUpload) {
      setNativeFile(null);
    }
    if (fileRef == null) {
      setFileName(null);
      return;
    }

    if (!("current" in fileRef) || !("files" in fileRef.current)) {
      setFileName(null);
      return;
    }

    if (fileRef.current.files.length < 1) {
      setFileName(null);
      return;
    }

    setFileName(fileRef.current.files[0].name);
  }, [useNativeIosUpload]);

  useEffect(() => {
    const fileInput = fileRef.current;
    if (!useNativeIosUpload && fileInput != null) {
      fileInput.addEventListener("change", onInputChange);
    }

    getList((oGet, id) => {
      setDocumentStatus(oGet("documentstatus"));
      setPendingphotos(oGet("pendingphotos"));
    });

    return () => {
      if (!useNativeIosUpload && fileInput != null) {
        fileInput.removeEventListener("change", onInputChange);
      }
    };
  }, [getList, onInputChange, useNativeIosUpload]);

  async function handleSubmit() {
    setButtonDisabled(true);
    setLoading(true);

    if (!useNativeIosUpload) {
      if (fileRef.current.files.length < 1) {
        setButtonDisabled(false);
        setLoading(false);
        setErrorMessage("Please select a file");
        return;
      }

      const typeRegex = /(jpeg|jpg)/;
      const fileType = fileRef.current.files[0].type;
      const fileSize = fileRef.current.files[0].size;

      if (typeRegex.test(fileType) === false) {
        setButtonDisabled(false);
        setLoading(false);
        setErrorMessage("Wrong file type. Only jpeg/jpg accepted");
        return;
      }

      if (fileSize > 10000000) {
        setButtonDisabled(false);
        setLoading(false);
        setErrorMessage("File too big. Maximum size 10MB");
        return;
      }

      if (fileSize < 50) {
        setButtonDisabled(false);
        setErrorMessage("Invalid file");
        setLoading(false);
        return;
      }

      try {
        const storeState = store.getState();
        const endPoint = `user.${storeState.services.ids.user}/documentupload`;
        const url = `${config.API.ADDRESS}?target=${endPoint}&docpost=t`;
        const formData = new FormData();
        formData.append("file", fileRef.current.files[0]);
        const response = await fetch(url, {
          method: "post",
          headers: {
            "x-omicron": storeState.services.api.JRR,
          },
          body: formData,
        });
        await response.json();
      } catch (error) {
        throw error;
      } finally {
        setErrorMessage("");
        setButtonDisabled(false);
        setLoading(false);
        window.location.reload();
      }
      return;
    }

    const file =
      nativeFile ||
      (fileRef.current != null && fileRef.current.files.length > 0
        ? fileRef.current.files[0]
        : null);

    if (file == null) {
      setButtonDisabled(false);
      setLoading(false);
      setErrorMessage("Please select a file");
      return;
    }

    if (file.size > MAX_UPLOAD_SIZE) {
      setButtonDisabled(false);
      setLoading(false);
      setErrorMessage("File too big. Maximum size 10MB");
      return;
    }

    let uploadFile;
    try {
      uploadFile = await normalizeUploadFile(file);
    } catch (e) {
      setButtonDisabled(false);
      setLoading(false);
      setErrorMessage(e.message || "Upload failed. Please try again.");
      return;
    }

    if (uploadFile == null) {
      setButtonDisabled(false);
      setLoading(false);
      setErrorMessage("Wrong file type. Choose a JPEG or PNG image.");
      return;
    }

    if (uploadFile.size > MAX_UPLOAD_SIZE) {
      setButtonDisabled(false);
      setLoading(false);
      setErrorMessage(
        "Converted JPEG is too big. Maximum upload size is 10MB."
      );
      return;
    }

    if (uploadFile.size < 50) {
      setButtonDisabled(false);
      setErrorMessage("Invalid file");
      setLoading(false);
      return;
    }

    try {
      const storeState = store.getState();
      const endPoint = `user.${storeState.services.ids.user}/documentupload`;

      //URL built from config file and specified end point (target)
      let url = `${config.API.ADDRESS}?target=${endPoint}&docpost=t`;

      const formData = new FormData();
      formData.append("file", uploadFile);

      //myInit is the second fetch() argument used to specify methods, headers, body...
      let myInit: init = {
        method: "post",
        headers: {
          "x-omicron": storeState.services.api.JRR,
        },
        body: formData,
      };

      const res = await fetch(url, myInit);
      const resjson = await res.json();
      if (!res.ok || resjson.error) {
        throw new Error(resjson.message || "Upload failed");
      }

      setErrorMessage("");
      window.location.reload();
    } catch (e) {
      console.error(e);
      setErrorMessage(e.message || "Upload failed. Please try again.");
    } finally {
      setButtonDisabled(false);
      setLoading(false);
    }
  }

  async function clickOnChoosePhoto() {
    if (useNativeIosUpload) {
      try {
        const file = await pickNativeIosPhoto();
        if (file != null) {
          setNativeFile(file);
          setFileName(file.name);
          setErrorMessage("");
        }
      } catch (e) {
        setErrorMessage(e.message || "Could not select a photo.");
      }
      return;
    }

    if (fileRef.current != null) {
      fileRef.current.click();
    }
  }

  if (loading) {
    return <LoadingBox />;
  }

  if (documentStatus === "C") {
    return (
      <GridContainer justify="center">
        <GridItem xs={12} md={10}>
          <Card>
            <CardHeader color="primary" icon>
              <CardIcon color="primary">
                <PostAddIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Document</h4>
            </CardHeader>
            <CardBody>
              <h4 className={classes.cardTitle}>
                The document(s) you submited were approved
                <br />
              </h4>

              <GridContainer align="center" justify="center">
                <GridItem xs={12}>
                  <Link to={config.MYACCOUNT.APP_PATH}>
                    <Button
                      className={classes.buttonStyle}
                      color="primary"
                      disabled={false}
                    >
                      Go to Main Screen
                    </Button>
                  </Link>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }

  if (pendingphotos >= 2) {
    return (
      <GridContainer justify="center">
        <GridItem xs={12} md={10}>
          <Card>
            <CardHeader color="primary" icon>
              <CardIcon color="primary">
                <PostAddIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Document</h4>
            </CardHeader>
            <CardBody>
              <h4 className={classes.cardTitle}>
                The document(s) you submited are waiting for approval. Please
                check again later.
                <br />
              </h4>

              <GridContainer align="center" justify="center">
                <GridItem xs={12}>
                  <Link to={config.MYACCOUNT.APP_PATH}>
                    <Button
                      className={classes.buttonStyle}
                      color="primary"
                      disabled={false}
                    >
                      Go to Main Screen
                    </Button>
                  </Link>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }

  return (
    <GridContainer justify="center">
      <GridItem xs={12} md={10}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <PostAddIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Document</h4>
          </CardHeader>
          <CardBody>
            {pendingphotos >= 1 ? (
              <GridContainer align="center" justify="center">
                <GridItem xs={12}>
                  <strong>
                    We have received your first document, you can go back to the
                    checklist or submit a second one.
                    <br />
                  </strong>
                </GridItem>

                <GridItem xs={12}>
                  <Link to={config.MYACCOUNT.APP_PATH}>
                    <Button
                      className={classes.buttonStyle}
                      color="primary"
                      disabled={false}
                    >
                      Go to Main Screen
                    </Button>
                  </Link>
                </GridItem>

                <br />
              </GridContainer>
            ) : null}
            <h4 className={classes.cardTitle}>
              We require a document to confirm your identity. <br />
              <br />
              Take a picture or scan <strong>TWO</strong> of the following
              documents that contains your legal name{" "}
              {useNativeIosUpload
                ? "(JPEG or PNG accepted; PNG photos will be converted to JPEG, max 10MB)"
                : "(only jpeg/jpg accepted, max 10MB)"}
              <br />
              <font color="green">
                <br />
                You <strong>CAN</strong> submit:
                <br />
                -Gas bill <br />
                -Electric bill <br />
                -Phone bill <br />
                -Municipal tax payment receipt
                <br />
                -Municipal utilities bill
                <br />
              </font>
              <font color="red">
                <br />
                Do <strong>NOT</strong> submit:
                <br />
                -Driver's license <br />
                -Passport <br />
                -Credit Card statement <br />
                -Income tax return <br />
                -Any document that has sensitive information such as your date
                of birth, social insurance number or driver's license number.
              </font>
            </h4>

            <GridContainer align="center" justify="center">
              <GridItem xs={12}>
                <br />
                <Button
                  className={classes.buttonStyle}
                  color={fileName == null ? "warning" : "success"}
                  onClick={clickOnChoosePhoto}
                >
                  Upload document
                </Button>
                <font color="red">{errorMessage}</font>
                {fileName == null ? null : <p>{fileName}</p>}
                <input
                  className={classes.fileInput}
                  type="file"
                  capture={useNativeIosUpload ? undefined : "environment"}
                  id="file1"
                  name="file1"
                  accept={
                    useNativeIosUpload
                      ? "image/jpeg,image/png,.jpg,.jpeg,.png"
                      : ".jpg, .jpeg"
                  }
                  onChange={useNativeIosUpload ? onInputChange : undefined}
                  ref={fileRef}
                />
                <br />
                <br />
                <Button
                  className={classes.buttonStyle}
                  color="primary"
                  disabled={buttonDisable}
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

export default withStyles(styles)(
  loadContent("user.session/documentupload", DocumentUpload)
);
